import { useEffect } from 'react';
import type { JSX, ReactNode } from 'react';
import {
  useActivationSections,
  useSeedActivationReports,
  useSeedAnalyses,
  useSeedExternalReports,
  useSrConfiguration,
  useSrPartners,
} from '@/data/queries';
import { useSessionStore } from '@/state/sessionStore';
import { Card, EmptyState, Notice } from '../primitives';

/**
 * Hydrates the session store from the data adapter, once.
 *
 * This is the only place the seed collections are read. Everything downstream
 * works from the store, which is what lets an agent save a run and have the
 * library reflect it without a refetch.
 *
 * The gate is real, not cosmetic: derived KPIs need the SR configuration and
 * the activation section catalogue to be present, so the children do not render
 * until all six reads resolve. Under the static adapter that is one microtask.
 */
export function SessionBootstrap({ children }: { readonly children: ReactNode }): JSX.Element {
  const analyses = useSeedAnalyses();
  const external = useSeedExternalReports();
  const activation = useSeedActivationReports();
  const srConfig = useSrConfiguration();
  const srPartners = useSrPartners();
  const sections = useActivationSections();

  const hydrate = useSessionStore((s) => s.hydrate);
  const hydrated = useSessionStore((s) => s.hydrated);

  const ready =
    analyses.data !== undefined &&
    external.data !== undefined &&
    activation.data !== undefined &&
    srConfig.data !== undefined &&
    srPartners.data !== undefined &&
    sections.data !== undefined;

  useEffect(() => {
    if (!ready || hydrated) return;
    hydrate({
      analyses: analyses.data,
      externalReports: external.data,
      activationReports: activation.data,
      srConfig: srConfig.data,
      srPartners: srPartners.data,
      sections: sections.data,
    });
  }, [
    ready,
    hydrated,
    hydrate,
    analyses.data,
    external.data,
    activation.data,
    srConfig.data,
    srPartners.data,
    sections.data,
  ]);

  const error =
    analyses.error ??
    external.error ??
    activation.error ??
    srConfig.error ??
    srPartners.error ??
    sections.error;

  if (error) {
    return (
      <div style={{ padding: 32 }}>
        <Notice tone="bad">
          <b>Could not load the session data.</b>{' '}
          {error instanceof Error ? error.message : 'Unknown error.'}
        </Notice>
      </div>
    );
  }

  if (!hydrated) {
    return (
      <div style={{ padding: 32 }}>
        <Card>
          <EmptyState icon="refresh" title="Loading session" caption="Reading the seed data." />
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
